import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "search" }
const _hoisted_2 = { class: "albums-container" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "actions" }
const _hoisted_5 = { class: "submit-btn-wrapper" }
const _hoisted_6 = {
  key: 0,
  class: "remove-notice"
}

import { computed, ref, onMounted, nextTick, markRaw, useTemplateRef } from 'vue';

import Fuse from 'fuse.js';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';

import AlbumForm from './AlbumForm.vue';
import AlbumsList from './AlbumsList.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

import * as dav from '@services/dav';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import { n, t } from '@services/l10n';

import type { IAlbum, IPhoto } from '@typings';

import PlusIcon from 'vue-material-design-icons/Plus.vue';
import CheckIcon from 'vue-material-design-icons/Check.vue';
import MagnifyIcon from 'vue-material-design-icons/Magnify.vue';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'AlbumPicker',
},
  __name: 'AlbumPicker',
  props: {
    photos: {},
    disabled: { type: Boolean },
    initialSelection: {}
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const root = useTemplateRef<HTMLDivElement>('root');

const showAlbumCreationForm = ref(false);
const loadingAlbums = ref(0);
/** List of all albums */
const albums = ref<IAlbum[]>([]);
/** Search provider for list to show */
const fuse = ref<Fuse<IAlbum> | null>(null);
/** Initial selection */
const initSelection = ref(new Set<IAlbum>());
/** Selected albums */
const selection = ref(new Set<IAlbum>());
/** Deselected albums that were initially selected */
const deselection = ref(new Set<IAlbum>());
/** Search term */
const search = ref(String());
/** Abort controller */
const abort = useAbort();

const filteredList = computed(() => {
  if (!albums.value || !search.value || !fuse.value) return albums.value ?? [];
  return fuse.value.search(search.value).map((r) => r.item);
});

onMounted(async () => {
  void loadAlbums();
  await nextTick();
  // prevent autofocus on search bar for mobile
  (root.value?.closest('.modal-mask') as HTMLElement | null)?.focus?.();
});

async function albumCreatedHandler({ album }: { album: { basename: string } }) {
  showAlbumCreationForm.value = false;
  await loadAlbums(true);

  // select the newly created album
  const newAlbum = albums.value.find((a) => a.name === album.basename);
  if (newAlbum) {
    selection.value.add(newAlbum);
  }
}

async function loadAlbums(preserveSelection: boolean = false) {
  const signal = abort.renew();
  try {
    loadingAlbums.value++;

    const prevSel = new Set(Array.from(selection.value).map((a) => a.album_id));
    const prevDesel = new Set(Array.from(deselection.value).map((a) => a.album_id));

    // get all albums
    albums.value = await dav.getAlbums({ signal });
    signal.throwIfAborted();

    // create search provider
    fuse.value = markRaw(new Fuse(albums.value, { keys: ['name'] }));

    // get initial selection
    let initSelIds: number[] = [];
    const singleFileId = props.photos.length === 1 ? props.photos[0].fileid : 0;

    if (props.initialSelection) {
      // check if selection was passed as a prop
      initSelIds = props.initialSelection.map((a) => a.album_id);
    } else if (singleFileId) {
      // if only one photo is selected, get the albums of that photo
      const pAlbums = await dav.getAlbums({ fileid: singleFileId, signal });
      signal.throwIfAborted();
      initSelIds = pAlbums.map((a) => a.album_id);
    }

    // initialize all sets
    initSelection.value = new Set(albums.value.filter((a) => initSelIds.includes(a.album_id)));
    selection.value = new Set(initSelection.value);
    deselection.value = new Set();

    // restore selection
    if (preserveSelection) {
      albums.value
        .filter((a) => prevSel.has(a.album_id))
        .forEach((a) => {
          selection.value.add(a);
        });
      albums.value
        .filter((a) => prevDesel.has(a.album_id))
        .forEach((a) => {
          selection.value.delete(a);
          deselection.value.add(a);
        });
    }
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
  } finally {
    loadingAlbums.value--;
  }
}

function toggleAlbumSelection(album: IAlbum) {
  if (props.disabled) return;

  if (selection.value.has(album)) {
    selection.value.delete(album);

    // deselection only if originally selected
    if (initSelection.value.has(album)) {
      deselection.value.add(album);
    }
  } else {
    selection.value.add(album);
    deselection.value.delete(album);
  }
}

function submit() {
  emit('select', Array.from(selection.value), Array.from(deselection.value));
}

return (_ctx: any,_cache: any) => {
  return (!showAlbumCreationForm.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "root",
        ref: root,
        class: "album-picker"
      }, [
        (loadingAlbums.value)
          ? (_openBlock(), _createBlock(XLoadingIcon, {
              key: 0,
              class: "loading-icon centered"
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(NcTextField), {
            autofocus: false,
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            label: _unref(t)('memories', 'Search'),
            placeholder: _unref(t)('memories', 'Search')
          }, {
            default: _withCtx(() => [
              _createVNode(MagnifyIcon, { size: 16 })
            ]),
            _: 1
          }, 8, ["modelValue", "label", "placeholder"])
        ]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(AlbumsList, {
            albums: filteredList.value,
            link: false,
            onClick: toggleAlbumSelection
          }, {
            extra: _withCtx(({ album }) => [
              _createElementVNode("div", {
                class: _normalizeClass({
              'album-selected': selection.value.has(album),
              'check-circle-icon': true,
              'check-circle-icon--active': selection.value.has(album),
            }),
                onClick: _withModifiers(($event: any) => (toggleAlbumSelection(album)), ["stop"])
              }, [
                _createVNode(CheckIcon, { size: 20 })
              ], 10, _hoisted_3)
            ]),
            _: 1
          }, 8, ["albums"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(NcButton), {
            "aria-label": _unref(t)('memories', 'Create new album.'),
            disabled: __props.disabled,
            class: "new-album-button",
            variant: "tertiary",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (showAlbumCreationForm.value = true))
          }, {
            icon: _withCtx(() => [
              _createVNode(PlusIcon)
            ]),
            default: _withCtx(() => [
              _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'Create new album')), 1)
            ]),
            _: 1
          }, 8, ["aria-label", "disabled"]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(NcButton), {
              class: "new-album-button",
              variant: "primary",
              "aria-label": _unref(t)('memories', 'Save changes'),
              disabled: __props.disabled,
              onClick: submit
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('memories', 'Save changes')), 1)
              ]),
              _: 1
            }, 8, ["aria-label", "disabled"]),
            (deselection.value.size > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(n)('memories', 'Removed from {n} album', 'Removed from {n} albums', deselection.value.size, {
              n: deselection.value.size,
            })), 1))
              : _createCommentVNode("", true)
          ])
        ])
      ], 512))
    : (_openBlock(), _createBlock(AlbumForm, {
        key: 1,
        "display-back-button": true,
        title: _unref(t)('memories', 'New album'),
        onBack: _cache[2] || (_cache[2] = ($event: any) => (showAlbumCreationForm.value = false)),
        onDone: albumCreatedHandler
      }, null, 8, ["title"]))
}
}

})